import { useState, useMemo } from 'react';
import Decimal from 'decimal.js';
import moment from "moment-timezone";
import { useNavigate } from 'react-router-dom';
import { Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/DataTable/index';
import { DataTableColumn } from '@/components/ui/DataTable/types';
import { RoutePath } from '@/section/routerPaths';
import { cn, CommonDestinationTimezone } from '@/lib/utils';
import { AttachCustomerModal } from './AttachCustomerModal';
import {
  TtOrderViewResult,
  TtOrderFilter,
  TtOrderRow,
  TtFillEventRow,
  TtFillEventsFilter,
  getFilteredOrders,
  getFilteredFillEvents,
} from '../transform';

type ViewMode = 'orders' | 'fill_events';

interface TTSnapshotOrdersTableProps {
  orderView: TtOrderViewResult;
  fillEvents: TtFillEventRow[];
  activeFilter: TtOrderFilter;
  onFilterChange: (filter: TtOrderFilter) => void;
  isLoading: boolean;
}

export function TTSnapshotOrdersTable({
  orderView,
  fillEvents,
  activeFilter,
  onFilterChange,
  isLoading,
}: TTSnapshotOrdersTableProps) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGuidOrderId, setSelectedGuidOrderId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('orders');
  const [fillEventsFilter, setFillEventsFilter] = useState<TtFillEventsFilter>('all');

  const filteredOrders = useMemo(
    () => getFilteredOrders(orderView, activeFilter),
    [orderView, activeFilter]
  );

    /* const filteredFillEvents = useMemo(
  *   () => getFilteredFillEvents(fillEvents, fillEventsFilter),
  *   [fillEvents, fillEventsFilter]
  * ); */


  const handleRowClick = (order: TtOrderRow) => {
    navigate(
      RoutePath.TTOrdersDetail.replace(':guid_order_id', encodeURIComponent(order.guid_order_id))
    );
  };

  const handleAttachClick = (e: React.MouseEvent, guidOrderId: string) => {
    e.stopPropagation();
    setSelectedGuidOrderId(guidOrderId);
    setModalOpen(true);
  };

  const columns: DataTableColumn<TtOrderRow>[] = useMemo(
    () => [
      {
        id: 'actions',
        header: 'Actions',
        size: 120,
        minSize: 100,
        disableRowClick: true,
        cell: (info) => {
          const order = info.row.original;
          // Only show attach button for orders missing customer ID
          if (!order.missing_data_reason) return null;
          return (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => handleAttachClick(e, order.guid_order_id)}
            >
              Attach Info
            </Button>
          );
        },
      },
      {
        id: 'exchange_event_time',
        accessorKey: 'exchange_event_time',
        header: 'First Event',
        size: 100,
        minSize: 80,
        columnFilterType: "date",
        filterDateTimezone: CommonDestinationTimezone,
        filterDisplayFn: (v) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD hh:mm a"),
        filterParseDateFn: (v) => moment.tz(v as string, CommonDestinationTimezone),
        cell: (info) => {
          const value = info.getValue() as string;
          return (
            moment.tz(value, CommonDestinationTimezone).format("MM/DD/YYYY hh:mm a")
          );
        },
      },
      {
        id: 'trading_account_name',
        accessorKey: 'trading_account_name',
        header: 'Account',
        size: 180,
        minSize: 120,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'user_name',
        accessorKey: 'user_name',
        header: 'User',
        size: 180,
        minSize: 120,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'instrument_name',
        accessorKey: 'instrument_name',
        header: 'Instrument',
        size: 240,
        minSize: 150,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'order_quantity',
        accessorKey: 'order_quantity',
        header: 'Order',
        maxSize: 90,
        minSize: 50,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'text_a',
        accessorKey: 'text_a',
        header: 'Text A',
        size: 120,
        minSize: 100,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'text_b',
        accessorKey: 'text_b',
        header: 'Text B',
        size: 120,
        minSize: 100,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'text_c',
        accessorKey: 'text_c',
        header: 'Text C',
        size: 100,
        minSize: 80,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'text_tt',
        accessorKey: 'text_tt',
        header: 'Text TT',
        size: 120,
        minSize: 100,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'specified_customer_id',
        accessorKey: 'specified_customer_id',
        accessorFn: (row) => row.metadata?.SpecifiedCustomers.map(item => item.Customer?.long_name ?? "").join(","),
        header: 'Customer',
        size: 100,
        minSize: 80,
      },
      {
        id: 'limit_price',
        accessorKey: 'limit_price',
        accessorFn: (row) => row.limit_price,
        header: 'Limit Price',
        size: 100,
        minSize: 80,
      },
      {
        id: 'remaining_quantity',
        accessorKey: 'remaining_quantity',
        header: 'Remain',
        size: 100,
        minSize: 80,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'filled_quantity',
        accessorKey: 'filled_quantity',
        header: 'Filled',
        size: 100,
        minSize: 80,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        id: 'working_quantity',
        accessorKey: 'working_quantity',
        header: 'Working Qty',
        size: 100,
        minSize: 80,
      },
      {
        id: 'source',
        accessorKey: 'source',
        header: 'Source',
        size: 100,
        minSize: 80,
        cell: (info) => {
          const value = info.getValue() as string;
          return (
            <Badge variant="outline" className="text-xs capitalize">
              {value}
            </Badge>
          );
        },
      },
    ],
    []
  );

  const defaultColumnVisibility = useMemo(
    () => ({
      trading_account_name: true,
      instrument_name: true,
      order_quantity: true,
      text_a: true,
      text_b: true,
      text_c: true,
      text_tt: true,
      source: true,
      actions: true,
      exchange_event_time: true,
    }),
    []
  );

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="p-4 border rounded-md">
          <p>Loading orders...</p>
        </div>
      ) : (
        <DataTable
          data={filteredOrders}
          columns={columns}
          enableSorting
          enableMultiSort
          enableMultiFilter
          enableColumnResizing
          defaultCondensed
          enableVirtualization
          virtualizationRowHeight={40}
          virtualizationOverscan={2}
          enableFiltering
          enableColumnVisibility
          enableColumnPinning
          enableExport
          defaultColumnVisibility={defaultColumnVisibility}
          defaultSorting={[{ id: "exchange_event_time", desc: true }]}
          emptyMessage="No orders found for this filter"
          stickyHeaders
          tableClassName="max-h-[calc(100vh-500px)] overflow-y-scroll"
          rowClickable
          onRowClick={handleRowClick}
        />
      )}

      {/* Attach Customer Modal */}
      {selectedGuidOrderId && (() => {
        const selectedOrder = filteredOrders.find(order => order.guid_order_id === selectedGuidOrderId);
        return (
          <AttachCustomerModal
            open={modalOpen}
            onOpenChange={setModalOpen}
            guidOrderId={selectedGuidOrderId}
            existingMetadata={selectedOrder?.metadata}
            instrumentName={selectedOrder?.instrument_name}
            totalQuantity={selectedOrder?.order_uom_quantity != null ? new Decimal(selectedOrder.order_uom_quantity) : null}
            orderQuantity={selectedOrder?.order_quantity != null ? new Decimal(selectedOrder.order_quantity) : null}
            remainingQuantity={selectedOrder?.remaining_quantity != null ? new Decimal(selectedOrder.remaining_quantity) : null}
            filledQuantity={selectedOrder?.filled_quantity != null ? new Decimal(selectedOrder.filled_quantity) : null}
            orderTextFields={{
              textA: selectedOrder?.text_a,
              textB: selectedOrder?.text_b,
              textC: selectedOrder?.text_c,
              textTT: selectedOrder?.text_tt,
            }}
          />
        );
      })()}
    </div>
  );
}
